import React, { useEffect, useState } from 'react';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Collapse from '@material-ui/core/Collapse';
import Divider from '@material-ui/core/Divider';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';

import { isSolutionVisible, SOLUTION_GATING_READY } from '../../../config/workspaceNav/solutions';

/**
 * Renders a PbDQ workspace sidebar from a navigation tree.
 *
 * ONE renderer for every workspace: Network, School and Teacher are
 * progressively smaller cuts of the Partner menu, so they differ by the tree
 * passed in, not by behaviour. Five copies would be five places to fix the same
 * expander bug.
 *
 * The tree is rendered recursively and takes any depth. The sheet uses three
 * (Level 1 / Level 2 / Level 3) — for example Solutions > COS Children
 * Assessment > Preview. Tabs are a separate thing: the sheet gives them their
 * own Tab Code / Tab Name columns, and they belong inside a page, not here.
 *
 * Styling and props are taken from the sidebar it replaces so the swap is not
 * also a visual change.
 *
 * @param {object[]} items    the navigation tree
 * @param {object}   ctx      { authUser, insightsPath, hasAnyProgramAccess, … }
 * @param {string[]} solutions unlocked solution codes
 * @param {object}   classes  the parent's useStyles output
 * @param {boolean}  isDrawerOpen
 * @param {function} onNavigate  (path) => void
 * @param {function} isActive    (path) => bool
 */
const WorkspaceNav = ({
    items = [],
    ctx = {},
    // null means "not a PbDQ plan" — isSolutionVisible reads that as do-not-gate.
    // Defaulting to [] instead would gate a sidebar that forgot to pass the prop
    // and hide every P1/LC row on it.
    solutions = null,
    classes = {},
    isDrawerOpen = true,
    onNavigate,
    isActive,
    currentPath = '',
    forceOpenCodes = [],
}) => {
    /** Resolve a field that may be a value or a (ctx) => value. */
    const val = (field) => (typeof field === 'function' ? field(ctx) : field);

    /**
     * Is this row shown?
     *
     * `visibleWhen` is the gate that applies today. The solution filter is only
     * added once the product-to-solution mapping exists — until then the tags
     * are documentation, and applying them would hide working menu items whose
     * solutions cannot yet resolve.
     */
    const isVisible = (row) => {
        if (row.visibleWhen && !row.visibleWhen(ctx)) return false;
        if (SOLUTION_GATING_READY && !isSolutionVisible(row.solutions, solutions)) return false;
        return true;
    };

    /** Visible children of a row, at any depth. */
    const kidsOf = (row) => (val(row.children) || []).filter(isVisible);

    /**
     * Does `currentPath` belong to `row` — either its own `path`, or one of its
     * `matchPrefixes`?
     *
     * `matchPrefixes` covers a row whose own path hands off to a DIFFERENT real
     * page not otherwise in the tree (e.g. Solutions > COS Children Assessment >
     * Preview redirects to /dq_pro/product/view/:code/preview) — without it, the
     * highlight drops to nothing the moment the redirect lands, reading as a
     * flicker from orange to blank rather than a stable "you are here".
     *
     * Matches on a whole segment, not a raw prefix, so `/a/b` does not claim
     * `/a/bc`.
     */
    const rowOwnsPath = (row) => {
        const path = val(row.path);
        const extraPrefixes = val(row.matchPrefixes) || [];
        const owns = Boolean(path && isActive && isActive(path))
            || extraPrefixes.some((p) => currentPath === p || currentPath.startsWith(`${p}/`));
        // `activeWhen` — rows that share one path (the National Dashboard's countries): only the
        // one whose condition holds is active. Opt-in; rows without it behave as before.
        if (row.activeWhen) return owns && Boolean(val(row.activeWhen));
        return owns;
    };

    /** Does `row` or anything beneath it point at `currentPath`? */
    const containsPath = (row) => {
        if (rowOwnsPath(row)) return true;
        return kidsOf(row).some(containsPath);
    };

    /** Is `row` or any descendant the active route? Drives the highlight. */
    const isBranchActive = (row) => {
        const kids = kidsOf(row);
        if (kids.length) {
            // A parent is also active on its OWN page (e.g. /dq_pro/solutions,
            // /dq_pro/solutions/readiness), which no child points at — that is
            // where the collapsed drawer's parent click lands. Exact match only:
            // the parent's prefix would otherwise claim pages the tree leaves
            // to no row at all.
            const ownPath = val(row.path);
            return kids.some(isBranchActive) || Boolean(ownPath && currentPath === ownPath);
        }
        return rowOwnsPath(row);
    };

    /**
     * Which branches are expanded. Seeded open along the whole ancestor chain of
     * the current path, so navigating straight to a Level 3 URL does not present
     * a collapsed menu with no indication of where you are.
     */
    const buildSeed = (rows, acc = {}) => {
        rows.filter(isVisible).forEach((row) => {
            const kids = kidsOf(row);
            if (!kids.length) return;
            if (kids.some(containsPath)) acc[row.code] = true;
            buildSeed(kids, acc);
        });
        return acc;
    };
    const seed = buildSeed(items);

    const [open, setOpen] = useState(seed);

    useEffect(() => {
        // Re-seed on navigation, without collapsing anything the user opened.
        setOpen((prev) => ({ ...seed, ...prev }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPath]);

    /**
     * Branches something outside the sidebar needs opened — the tutorial points
     * at sub-items, and a highlighted element inside a collapsed group is
     * invisible, which strands the tutorial on that step.
     */
    const forceKey = forceOpenCodes.join(',');
    useEffect(() => {
        if (!forceOpenCodes.length) return;
        setOpen((prev) => {
            const next = { ...prev };
            forceOpenCodes.forEach((code) => { next[code] = true; });
            return next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [forceKey]);

    const toggle = (code) => setOpen((prev) => ({ ...prev, [code]: !prev[code] }));

    /**
     * Row styling by depth. Level 1 carries the icon and the divider; deeper
     * levels are text-only and step further in. `subItem2` is optional — a
     * sidebar with no Level 3 never needs it, and falls back to `subItem`.
     */
    const rowClass = (depth, active) => {
        if (depth === 0) {
            return `${classes.item} ${!isDrawerOpen ? classes.itemCollapsed : ''} ${active ? classes.itemActive : ''}`;
        }
        const base = depth === 1 ? classes.subItem : (classes.subItem2 || classes.subItem);
        return `${base} ${active ? classes.subItemActive : ''}`;
    };

    /**
     * Label type by depth — smaller and lighter as it nests, so the three levels
     * read apart at a glance rather than only by indent. Sidebars that define no
     * nested label classes fall back to the one they have.
     */
    const labelClass = (depth) => {
        if (depth === 0) return classes.label;
        if (depth === 1) return classes.labelSub || classes.label;
        return classes.labelSub2 || classes.labelSub || classes.label;
    };

    const renderRows = (rows, depth) => rows.filter(isVisible).map((row) => {
        // Count children AFTER filtering — a parent whose children are all
        // hidden must not render an expander that opens onto nothing.
        const children = kidsOf(row);
        const hasChildren = children.length > 0;
        const path = val(row.path);
        const label = val(row.label);
        /*
         * A row that is listed but not selectable — it names something the
         * sheet defines, for which this workspace has no destination yet.
         * Rendered muted and inert rather than hidden, so the shape of the
         * menu still matches the sheet while never producing a click that
         * goes nowhere. Opt-in per row; every existing row is unaffected.
         */
        const disabled = !!val(row.disabled);
        const Icon = row.icon;
        const branchActive = isBranchActive(row);
        const expandColor = branchActive && depth === 0 ? '#ffffff' : 'var(--brand-primary, #FF6900)';

        return (
            <React.Fragment key={row.code}>
                <ListItem
                    id={row.id}
                    button
                    onClick={() => {
                        // Listed, but there is nothing to open — see disabled above.
                        if (disabled) return undefined;
                        // `onSelect` — a row that sets something before it navigates (a National
                        // country). Opt-in; rows without it behave as before.
                        if (row.onSelect) row.onSelect(ctx);
                        // Collapsed drawer has no room for a submenu, so the
                        // parent navigates instead of expanding.
                        if (hasChildren && isDrawerOpen) return toggle(row.code);
                        return path && onNavigate && onNavigate(path);
                    }}
                    className={rowClass(depth, branchActive)}
                    style={disabled ? { opacity: 0.55, cursor: 'default' } : undefined}
                >
                    {Icon && depth === 0 && (
                        <ListItemIcon className={classes.icon}>
                            <Icon style={{ color: branchActive ? '#ffffff' : 'var(--brand-primary, #FF6900)', fontSize: '22px' }} />
                        </ListItemIcon>
                    )}
                    {isDrawerOpen && (
                        <>
                            <ListItemText className={labelClass(depth)} primary={label} />
                            {hasChildren && !disabled && (open[row.code]
                                ? <ExpandLess style={{ color: expandColor, fontSize: depth === 0 ? '20px' : '18px' }} />
                                : <ExpandMore style={{ color: expandColor, fontSize: depth === 0 ? '20px' : '18px' }} />)}
                        </>
                    )}
                </ListItem>
                {depth === 0 && <Divider className={classes.divider} />}

                {hasChildren && (
                    <Collapse in={!!open[row.code] && isDrawerOpen} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            {renderRows(children, depth + 1)}
                        </List>
                    </Collapse>
                )}
            </React.Fragment>
        );
    });

    return <>{renderRows(items, 0)}</>;
};

export default WorkspaceNav;
